/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/utils/jwtUtils.ts
 * Release: v2.0.0 - Utility JWT per verifica scadenza token
 * Data: Ottobre 2025
 */

interface DecodedToken {
  exp: number;
  iat: number;
  userId?: string;
  email?: string;
  [key: string]: any;
}

/**
 * Decodifica un token JWT senza verificare la firma
 * (la verifica firma avviene lato backend)
 */
export function decodeJWT(token: string): DecodedToken | null {
  try {
    // JWT formato: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Token JWT non valido: formato errato');
      return null;
    }

    // Decodifica payload (parte centrale)
    const payload = parts[1];

    // Converti da base64url a stringa JSON
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Errore decodifica JWT:', error);
    return null;
  }
}

/**
 * Verifica se il token JWT è scaduto
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return true; // Se non decodificabile o senza exp, consideriamo scaduto
  }

  // exp è in secondi, Date.now() in millisecondi
  const now = Date.now() / 1000;
  return decoded.exp < now;
}

/**
 * Ottiene il tempo rimanente alla scadenza in secondi
 * Ritorna 0 se già scaduto
 */
export function getTokenTimeRemaining(token: string): number {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return 0;
  }

  const now = Date.now() / 1000;
  const remaining = decoded.exp - now;

  return remaining > 0 ? remaining : 0;
}

/**
 * Formatta i secondi rimanenti in formato leggibile
 * Es: "2h 30m", "45m 20s", "30s"
 */
export function formatTimeRemaining(seconds: number): string {
  if (seconds <= 0) {
    return 'Scaduto';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
}

/**
 * Ottiene la data di scadenza del token
 */
export function getTokenExpirationDate(token: string): Date | null {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return null;
  }

  return new Date(decoded.exp * 1000);
}
